<template>
    <ion-slides class="slides-container" pager="true" :options="slideOpts" @ionSlideWillChange="onSlideWillChange" ref="slides">
        <template v-for="slideContent of slideContents" :key="slideContent.headerText">
            <ion-slide>
                <div class="slide-container" v-show="showSlideContent">
                    <div class="slide-content" :class="{ 'onboarding-slide-content': isOnboarding }" :style="slideContent.audioSrc ? 'height:calc(100% - 115px)' : 'height:calc(100% - 50px)'">
                        <img v-if="slideContent.imageSrc && !isVideo(slideContent.imageSrc)" :src="slideContent.imageSrc" alt=""/>
                        <video v-if="slideContent.imageSrc && isVideo(slideContent.imageSrc)" autoplay muted loop playsinline @loadedmetadata="metadataLoaded" @canplaythrough="fullyLoaded">
                            <source :src="slideContent.imageSrc" :type="getContentType(slideContent.imageSrc)">
                        </video>
                        <vue3-markdown-it :source='slideContent.contentText' breaks />
                    </div>
                    <audio-player
                            :audio-data="slideContent.audioSrc"
                            :start-time-seconds="audioStartTime"
                            :end-time-seconds="audioEndTime"
                            class="popup-audio-player"
                            color="--color-primary">
                    </audio-player>   
                </div>
            </ion-slide>
        </template>
    </ion-slides>
</template>

<script lang="ts">
import { IonSlides, IonSlide } from "@ionic/vue";
import {computed, defineComponent, nextTick, onBeforeMount, ref, watch} from "vue";
import { useStore } from 'vuex';
import { SlideContent } from "@/types/types";
import AudioPlayer from '@/components/AudioPlayer.vue';
import {createLoadSpinner} from "@/services/ShareService";

export default defineComponent({
    name: "PopupSlides",
    components: {
        IonSlides,
        IonSlide,
        AudioPlayer
    },
    props: {
        slideContents: { type: Array as () => Array<SlideContent> },
        isOnboarding: { type: Boolean, default: false },
    },
    setup(props) {
        const store = useStore();
        const slideFooter = ref();
        const showSlideContent = ref(false);
        const useOnLoadedMetadata = computed(() => {
            const isSafari = navigator.userAgent.indexOf('Safari') != -1 && navigator.userAgent.indexOf('Chrome') == -1
            return isSafari || store.getters.platform() === 'ios';
        });
        const slideOpts = {
            initialSlide: 0,
            threshold: 12,
            pagination: {
                el: ".swiper-pagination",
                type: "bullets",
                clickable: true
            }
        };
        const slides = ref();
        const audioStartTime = ref(0);
        const audioEndTime = ref(0);
        const loadingSpinner = ref();
        const numVideos = props.slideContents?.filter((slide) => isVideo(slide.imageSrc ?? '')).length || 0;
        let numVideosLoaded = 0;

        onBeforeMount(async () => {
            if (numVideos > 0) {
                loadingSpinner.value = await createLoadSpinner(false);
                if (!showSlideContent.value) {
                    await loadingSpinner.value.present();
                }
            } else {
                showSlideContent.value = true;
            }
        });

        watch(showSlideContent, async (visible) => {
            if (!visible || !props.isOnboarding) {
                return;
            }
            await nextTick();
            resetOnboardingSlideScroll();
            setTimeout(resetOnboardingSlideScroll, 80);
            setTimeout(resetOnboardingSlideScroll, 180);
        });

        function pauseAudio() {
            store.dispatch("setShouldPauseAudio", true);
        }

        function onSlideWillChange() {
            pauseAudio();
            if (props.isOnboarding) {
                setTimeout(resetOnboardingSlideScroll, 50);
            }
        }

        function metadataLoaded() {
            if (useOnLoadedMetadata.value) {
                videoLoaded();
            }
        }

        function fullyLoaded() {
            if (!useOnLoadedMetadata.value) {
                videoLoaded();
            }
        }

        function videoLoaded() {
            numVideosLoaded += 1;
            if (numVideos === numVideosLoaded) {
                showSlideContent.value = true;
                loadingSpinner.value?.dismiss();
            }
        }

        function resetOnboardingSlideScroll() {
            if (!props.isOnboarding) {
                return;
            }
            const container = slides.value?.$el as HTMLElement | undefined;
            if (!container) {
                return;
            }
            const contentEls = container.querySelectorAll(".slide-content");
            contentEls.forEach((el) => {
                (el as HTMLElement).scrollTop = 0;
            });
        }

        function isVideo(dataUrl: string) {
            return dataUrl.split(':')[1].split('/')[0] === 'video';
        }

        function getContentType(dataUrl: string) {
            return dataUrl.split(';')[0].split(':')[1];
        }

        return {
            videoLoaded,
            slideOpts,
            audioStartTime,
            audioEndTime,
            pauseAudio,
            onSlideWillChange,
            slides,
            slideFooter,
            showSlideContent,
            useOnLoadedMetadata,
            metadataLoaded,
            fullyLoaded,
            isVideo,
            getContentType
        };
    },
});
</script>

<style scoped>
.modal-toolbar {
    --background: var(--color-app-background-rgb);
}

.slides-container {
    height: 100%; 
}

::v-deep(.swiper-pagination-bullet-active) {
    background: var(--color-primary) !important;
}

::v-deep(.swiper-pagination-bullet) {
    background: var(--color-light-grey) 0% 0% no-repeat padding-box;
    opacity: 1;
    font-size: 20px;
    width: 12px;
    height: 12px;
}

.slide-container {
    height: 100%;
}
.slide-content {
    -webkit-transform: translate3d(0,0,0);
    display: flex;
    flex-direction: column;
    align-items: start;
    /* padding-top: 32px; */
    padding-left: 36px;
    padding-right: 36px;
    overflow-y: auto;
    scrollbar-width: none;
}

.slide-content.onboarding-slide-content {
    padding-top: 14px;
}

.slide-content::-webkit-scrollbar {
    display: none;
}

.popup-audio-player {
    width: 90%;
    margin-left:auto;
    margin-right:auto;
}

.popup-slide-footer {
    /* padding-top: 15px; */
    width: 100%;
    background: white;
}

ion-icon {
    width: 100%;
    color: var(--color-primary);
}

video {
    width: 100%;
    border-radius: 20px;
    /* border:thin solid var(--color-primary); */
}
</style>
